/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from "react"
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Typography, FormControlLabel, Checkbox, Paper } from "@mui/material"
import { getDocs, collection, query, where, QuerySnapshot, DocumentData, orderBy, onSnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import { products } from "./lib/common"
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import ShippingDestinationForm, { ShipmentProductSnapshot } from "./lib/ShippingDestinationForm"
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

interface IPartPrice {
  id: string
  part: string
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number
  chilledPrice: number
  taxRate?: 8 | 10
  description: string
}


// 直近一年の日付範囲を計算する関数
const getInitialDateRange = () => {
  const today = dayjs();
  const oneYearAgo = today.subtract(1, 'year');
  return { startDate: oneYearAgo, endDate: today };
};

const SearchItems = () => {
  const [part, setPart] = useState<string>('')
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [items, setItems] = useState<Array<DocumentData>>()
  const [openShipping, setOpenShipping] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showUnshippedOnly, setShowUnshippedOnly] = useState<boolean>(false)
  const [partPrices, setPartPrices] = useState<IPartPrice[]>([])

  // 部位別価格マスタの取得
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'partPrices'), (snapshot) => {
      const prices = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as IPartPrice))
      setPartPrices(prices)
    })
    return unsubscribe
  }, [])

  // 価格を計算する関数
  const calculatePrice = (item: any): number => {
    const partPrice = partPrices.find(p => p.part === item.part)
    if (!partPrice) return 0

    // 重量をグラムから取得（文字列の場合は数値に変換）
    const weightInGrams = typeof item.weight === 'string' ?
      parseFloat(item.weight.replace(/[^\d.-]/g, '')) || 0 :
      (typeof item.weight === 'number' ? item.weight : 0)

    // グラムをキログラムに変換
    const weightInKg = weightInGrams / 1000

    // 温度状態に応じた価格を取得（未設定の場合は冷凍）
    const pricePerUnit = (item.storageType === '冷凍' || !item.storageType) ? partPrice.frozenPrice : partPrice.chilledPrice

    // 価格単位に応じて計算
    if (partPrice.priceUnit === 'perPiece') {
      // 1個あたりの価格（四捨五入）
      return Math.round(pricePerUnit)
    } else {
      // kg単位の価格（グラムをkgに変換してから掛けて四捨五入）
      return Math.round(pricePerUnit * weightInKg)
    }
  }

  // 部位に設定された消費税率を取得する関数（未設定の部位は8%とみなす）
  const getTaxRate = (item: any): number => {
    const partPrice = partPrices.find(p => p.part === item.part)
    return partPrice?.taxRate === 10 ? 10 : 8
  }

  // 日付範囲フィルターの初期値を設定（直近一年）
  const [dateRange, setDateRange] = useState(() => {
    const savedDateRange = sessionStorage.getItem('searchItemsDateRange');
    if (savedDateRange) {
      const parsed = JSON.parse(savedDateRange);
      return {
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null
      };
    }
    return getInitialDateRange();
  });

  // 開始日の変更ハンドラー
  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, startDate: newDate };
    setDateRange(newDateRange);
    // sessionStorageに保存
    sessionStorage.setItem('searchItemsDateRange', JSON.stringify({
      startDate: newDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString()
    }));
  };

  // 終了日の変更ハンドラー
  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    const newDateRange = { ...dateRange, endDate: newDate };
    setDateRange(newDateRange);
    // sessionStorageに保存
    sessionStorage.setItem('searchItemsDateRange', JSON.stringify({
      startDate: dateRange.startDate?.toISOString(),
      endDate: newDate?.toISOString()
    }));
  };
  const columns = [
    {
      field: "No",
      headerName: "No",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "individualNo",
      headerName: "個体識別番号",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "productNo",
      headerName: "製品番号",
      width: 200,
      cellClassName: "name-column--cell",
    },
    {
      field: "processingDate",
      headerName: "加工日",
      flex: 1,
    },
    {
      field: "part",
      headerName: "部位",
      flex: 1,
    },
    {
      field: "taxRate",
      headerName: "税率",
      width: 70,
      renderCell: (params: any) => `${getTaxRate(params.row)}%`,
    },
    {
      field: "weight",
      headerName: "重量",
      flex: 1,
    },
    {
      field: "storageType",
      headerName: "温度状態",
      width: 100,
    },
    {
      field: "price",
      headerName: "価格（税込）",
      width: 130,
      renderCell: (params: any) => {
        const price = Math.round(calculatePrice(params.row))
        return price > 0 ? `¥${price.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}` : '-'
      },
    },
    {
      field: "products",
      headerName: "出荷情報",
      flex: 1,
      renderCell: (params: any) => {
        if (params.row.isSold) {
          // 出荷済みの場合は出荷先を表示
          const destination = params.row.customer || '出荷先不明';
          return (
            <Chip
              label={destination}
              color="success"
              size="small"
              variant="outlined"
            />
          );
        } else {
          // 未出荷の場合は出荷ボタンを表示
          return (
            <Chip
              label="出荷する"
              color="default"
              size="small"
              onClick={() => shipping(params.row.id)}
            />
          );
        }
      },
    },
    {
      field: "detail",
      headerName: "個体詳細",
      flex: 1,
      renderCell: (params: any) => (
        <Chip label="詳細" color="default" size="small" onClick={() => window.open(`/individuals/${params.row.individualNo}`, '_blank')}></Chip>
      ),
    }
  ]

  const shipping = (productId: string) => {
    setSelectedProductId(productId)
    setOpenShipping(true)
  }

  const search = () => {
    if(!part) return

    // 日付範囲の妥当性チェック
    if (dateRange.startDate && dateRange.endDate && dateRange.startDate.isAfter(dateRange.endDate)) {
      setErrorMessage('開始日は終了日より前の日付を選択してください')
      return
    }

    setErrorMessage('')
    setItems(undefined)

    // クエリ条件を配列で管理
    const whereConditions = [
      where('part', '==', part)
    ]

    // 日付範囲フィルターを追加（データベースの形式 'YYYY/MM/DD' に合わせる）
    if (dateRange.startDate && dateRange.endDate) {
      const startDateStr = dateRange.startDate.format('YYYY/MM/DD');
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY/MM/DD');
      whereConditions.push(
        where('processingDate', '>=', startDateStr),
        where('processingDate', '<', nextDayStr)
      )
    } else if (dateRange.startDate) {
      const startDateStr = dateRange.startDate.format('YYYY/MM/DD');
      whereConditions.push(where('processingDate', '>=', startDateStr))
    } else if (dateRange.endDate) {
      const nextDayStr = dateRange.endDate.add(1, 'day').format('YYYY/MM/DD');
      whereConditions.push(where('processingDate', '<', nextDayStr))
    }

    // クエリを構築
    const searchQuery = query(
      collection(db, 'products'),
      ...whereConditions,
      orderBy("processingDate", "asc")
    )

    getDocs(searchQuery).then((_: QuerySnapshot) => {
      const allItems = _.docs.map((doc: DocumentData) => ({ id: doc.id, ...doc.data() }))

      // 製品番号で重複を除去（最新の加工日のレコードを保持）
      const uniqueItems = allItems.reduce((acc: any[], current: any) => {
        const existingIndex = acc.findIndex(item => item.productNo === current.productNo)

        if (existingIndex === -1) {
          // 新しい製品番号の場合は追加
          acc.push(current)
        } else {
          // 既存の製品番号がある場合は、より新しい加工日のレコードを保持
          const existingDate = new Date(acc[existingIndex].processingDate.replace(/\//g, '-'))
          const currentDate = new Date(current.processingDate.replace(/\//g, '-'))

          if (currentDate > existingDate) {
            acc[existingIndex] = current
          }
        }

        return acc
      }, [])

      // 未出荷フィルターを適用
      const filteredItems = showUnshippedOnly
        ? uniqueItems.filter((item: any) => !item.isSold)
        : uniqueItems

      // No列用の連番を追加
      const items = filteredItems.map((item: any, i: number) => ({ No: i+1, ...item }))

      items.length > 0 ? setItems(items) : setErrorMessage('該当する商品情報がありません')
    }).catch((error) => {
      console.error('検索エラー:', error)
      setErrorMessage('検索中にエラーが発生しました。しばらく時間をおいて再度お試しください。')
    })
  }

  const onChangePart = ({ target: { value } }: any) => {
    setPart(value)
  }

  const closeShippingForm = () => {
    setSelectedProductId('')
    setOpenShipping(false)
  }

  // 総重量を計算する関数
  const totalWeight = useMemo(() => {
    if (!items) return 0;

    return items.reduce((sum, item) => {
      // 重量が文字列の場合は数値に変換、数値でない場合は0として扱う
      const weight = typeof item.weight === 'string' ?
        parseFloat(item.weight.replace(/[^\d.-]/g, '')) || 0 :
        (typeof item.weight === 'number' ? item.weight : 0);
      return sum + weight;
    }, 0);
  }, [items]);

  // 総額を計算する関数
  const totalPrice = useMemo(() => {
    if (!items || partPrices.length === 0) return 0;

    const total = items.reduce((sum, item) => {
      const price = calculatePrice(item)
      return sum + price;
    }, 0);

    return Math.round(total);
  }, [items, partPrices]);

  return (
    <Box display="contents" justifyItems="center">
      <Box display="flex" justifyContent="center" mt={2}>
        <Typography variant="h4">商品検索</Typography>
      </Box>

      {/* フィルター（横並びレイアウト・センター寄せ） */}
      <Box display="flex" justifyContent="center" mt={3}>
        <Box display="flex" alignItems="end" gap={3} flexWrap="wrap">
          {/* 日付範囲フィルター */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>加工日期間フィルター</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
              <Box display="flex" alignItems="center" gap={1}>
                <DatePicker
                  label="開始日"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small', sx: { width: '140px' } } }}
                />
                <Typography>～</Typography>
                <DatePicker
                  label="終了日"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small', sx: { width: '140px' } } }}
                />
              </Box>
            </LocalizationProvider>
          </Box>

          {/* 部位選択 */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>部位</Typography>
            <FormControl sx={{ width: '200px' }}>
              <InputLabel id="part-select-label">部位</InputLabel>
              <Select
                labelId="part-select-label"
                id="part-select"
                label="部位"
                placeholder="部位"
                onChange={(e) => onChangePart(e)}
                size="small"
                value={part}
              >
                {
                  products.map((product, i) => {
                    return <MenuItem key={i} value={product}>{product}</MenuItem>
                  })
                }
              </Select>
            </FormControl>
          </Box>

          {/* 未出荷フィルター */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>出荷状況</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showUnshippedOnly}
                  onChange={(e) => setShowUnshippedOnly(e.target.checked)}
                  size="small"
                />
              }
              label="未出荷のみ表示"
            />
          </Box>
        </Box>
      </Box>
      <Box display="flex-col" m={1} justifyItems="center">
        <Box display="flex" justifyContent="center">
          <Typography variant="subtitle2" color="red">{errorMessage}</Typography>
        </Box>
        <Box display="flex" justifyContent="center" mt={2}>
          <Box><Button variant="contained" onClick={() => search()}>検索</Button></Box>
        </Box>
      </Box>
      {
        items && (
          <Box display="flex" flexDirection="column" alignItems="center" mt={3} gap={2}>
            {/* 総重量・総額表示 */}
            <Paper elevation={2} sx={{ padding: 2, minWidth: '400px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
                <Typography variant="h6" color="primary">
                  検索結果: {items.length}件
                </Typography>
                <Typography variant="h6" color="secondary">
                  総重量: {totalWeight.toFixed(1)}kg
                </Typography>
                <Typography variant="h6" color="success.main">
                  総額: ¥{totalPrice.toLocaleString('ja-JP', { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            </Paper>

            {/* データグリッド */}
            <Box sx={{ width: '100%', maxWidth: '1200px' }}>
              <DataGrid
                rows={items}
                columns={columns}
                sx={{ minWidth: '800px', width: '100%' }}
              />
            </Box>
          </Box>
        )
      }
      <ShippingDestinationForm
        productId={selectedProductId}
        productSnapshots={
          selectedProductId && items
            ? items
                .filter((it: any) => it.id === selectedProductId)
                .map<ShipmentProductSnapshot>((it: any) => ({
                  id: it.id,
                  productNo: it.productNo,
                  individualNo: it.individualNo,
                  part: it.part,
                  weight: it.weight,
                  storageType: it.storageType,
                  quantity: 1,
                  price: calculatePrice(it),
                  taxRate: getTaxRate(it),
                }))
            : []
        }
        isOpen={openShipping}
        onClose={() => closeShippingForm()}
      />
    </Box>
  )
}

export default SearchItems
